import { useEffect, useRef } from "react";
import { Box, Text } from "@mantine/core";
import { RichTextEditor } from "@mantine/tiptap";
import Placeholder from "@tiptap/extension-placeholder";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

interface ReportFooterEditorProps {
  value: string;
  onChange: (value: string) => void;
}

const REPORT_FOOTER_EXTENSIONS = [
  StarterKit.configure({
    blockquote: false,
    bulletList: false,
    code: false,
    codeBlock: false,
    heading: false,
    horizontalRule: false,
    link: false,
    listItem: false,
    listKeymap: false,
    orderedList: false,
    strike: false,
  }),
  Placeholder.configure({
    placeholder: "Enter the report footer text",
  }),
];

export function ReportFooterEditor({
  value,
  onChange,
}: ReportFooterEditorProps) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    extensions: REPORT_FOOTER_EXTENSIONS,
    content: value,
    onUpdate: ({ editor: updatedEditor }) => {
      onChangeRef.current(updatedEditor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed || editor.getHTML() === value) return;
    editor.commands.setContent(value || "", { emitUpdate: false });
  }, [editor, value]);

  return (
    <Box>
      <Text size="sm" fw={500} mb={4}>
        Report Footer
      </Text>
      <Text size="xs" c="dimmed" mb={6}>
        Optional. Formatting and line breaks appear in reports when this
        organization is the configured identity source.
      </Text>
      <RichTextEditor editor={editor}>
        <RichTextEditor.Toolbar>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Undo />
            <RichTextEditor.Redo />
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>
        <RichTextEditor.Content
          aria-label="Report Footer"
          style={{ minHeight: 120 }}
        />
      </RichTextEditor>
    </Box>
  );
}
